/**
 * @file enrollpilot-logo.tsx
 * @description EnrollPilot header lockup: the authentic marketing-site brand
 *   mark (cropped from brand_assets, theme-swapped) paired with a live Outfit
 *   two-tone "Enroll"/"Pilot" wordmark sized for the app header.
 * @author William Donaldson
 * @last_modified 2026-07-11
 */

"use client";

import Image from "next/image";

import { cn } from "@/lib/utils";

/**
 * The EnrollPilot brand mark (ascending emerald spearhead with orbital swoosh
 * + pixel trail), cropped from the marketing-site logo.
 *
 * WHY two theme-swapped rasters: the light-theme mark carries dark-navy detail
 * that reads on white headers, while the dark-theme mark carries lighter detail
 * that reads on navy/slate/cyberpunk headers. Tailwind's `.dark` class (toggled
 * by applyTheme for every isDark theme) selects the right one with no JS/flash.
 * Assets are rebuilt by brand_assets/build-logo-marks.mjs.
 */
export function EnrollPilotMark({ className }: { className?: string }) {
  return (
    <>
      <Image
        src="/brand/enrollpilot-mark-light.png"
        alt="EnrollPilot"
        width={329}
        height={334}
        className={cn("h-7 w-7 object-contain dark:hidden", className)}
      />
      <Image
        src="/brand/enrollpilot-mark-dark.png"
        alt=""
        aria-hidden
        width={179}
        height={182}
        className={cn("hidden h-7 w-7 object-contain dark:block", className)}
      />
    </>
  );
}

/**
 * Two-tone "Enroll" + "Pilot" wordmark in Outfit (the marketing brand face).
 * "Enroll" uses text-foreground so it stays legible on light and dark themes;
 * "Pilot" holds the brand emerald (brightened slightly on dark for contrast).
 */
export function EnrollPilotWordmark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "font-[family-name:var(--font-outfit)] text-[24px] font-semibold leading-none tracking-tight",
        className
      )}
    >
      <span className="text-foreground">Enroll</span>
      <span className="text-[#0a9b72] dark:text-[#12b886]">Pilot</span>
    </span>
  );
}

/** Full horizontal lockup: mark + wordmark. */
export function EnrollPilotLogo({ className }: { className?: string }) {
  return (
    <span className={cn("flex items-center gap-2", className)}>
      <EnrollPilotMark className="shrink-0" />
      <EnrollPilotWordmark />
    </span>
  );
}
